'use client';
// import "./styles.css";
import { useEffect, useRef, useState } from "react";
import { AppBar, Toolbar, Container, createTheme, ThemeProvider, Typography, Select, MenuItem, Button, TextField } from "@mui/material";
import { BasicMenu } from './basic-menu';
import { PasswordChanger } from "./password-changer";
import { BasicLoginBar } from "./basic-login-bar";
import { CustomerCreator, CustomerEditor, CustomerDeletor } from "./customers-management/customer-creator";
import { ReportGenerator } from "./customers-management/report-generator";
import StorylineServerProxy  from "./storyline-proxy/storyline-proxy";
import { UserProfileForm } from "./user-profile";
import { ScraperCreator, ScrapersEditor } from './scrapers-management/scraper-creator';
import ScrapReportsPage from './scrap-reports-management/scrap-reports-list';
import { StoryElementsList } from "./storyelements-management/story-element";
import RunScrapers from "./scrapers-management/run-scrapers";
import { JsonPostQueryHelper } from "./utils";
import ChannelsSetterPage from "./channeling-management/channels-setter";
import Newsletters from "./channeling-management/newsletters";
import ElementsForSocialMedia from "./channeling-management/social";
import TEDsDownloadPage from "./TEDs/TEDs-page";
import PendingTEDsList from "./TEDs/pending-list";
import DSNAChannelSetterPage from "./channeling-management/dsna-rss-setter";
import { LinkDetails } from "./scrap-reports-management/link-details";
import { ManualArchiveManagement } from "./storyelements-management/manual-archive-management";
import { AutomaticArchiveManagement } from "./storyelements-management/automatic-archive-management";
import { RobotsList } from "./robots-management/robots-list";
import ScrapReportAdvanced from "./scrap-reports-management/scrap-reports-advanced";
import TEDFiltersPage from "./TEDs/TED-filter-ui";
import StorylineSearch from "./storyline-search/storyline-search";
import { ScrapersMassImport } from "./scrapers-management/scrapers-mass-importer";
import { MultiStorylineReport } from "./customers-management/multi-storyline-report";
import { DigestGenerator } from "./storyelements-management/digest-generator";
import ScrapReportsPageClassic from "./scrap-reports-management/scrap-reports-list-classic";
import GARSetterPage from "./channeling-management/gar-setter";
import { GARExtraLinksList } from "./gar-extra-management/gar-extra-links";
import EngageChannelSetterPage from "./channeling-management/engage-setter";

export default function App() {

  type PageNamesType = 
    "No page" |
    "User > Profile" | "User > Change password" |
    "Storyline Search" |
    "Scrapers > Create" | "Scrapers > Edit / delete" | "Scrapers > Run / check status" | "Scrapers > Mass import" | "Scrapers > Advanced" | 
    "Storyline > View" | "Storyline > Scrap reports (classic)" | "Storyline > Scrap reports" | "Storyline > Failed scrap reports" | "Storyline > Pending TEDs" | "Storyline > Manual creation" | "Storyline > Digest generator" |
    "Archiving > Automatic" | "Archiving > Manual" |
    "Customers > Create" | "Customers > Edit" |  "Customers > Generate report" |  "Customers > Multi-storyline report" | "Customers > Delete customer" | 
    "Channels > Set" | "Channels > DSNA" | "Channels > Engage" | "Channels > Newsletters" | "Channels > Social media" | 
    "GAR > Pick news" | "GAR > Pick jobs" | "GAR > Extra links" |
    "TEDs > Download" | "TEDs > Processed TEDs" | "TEDs > Filters" |
    "Robots > Status"
  

  let [selectedPageName, setSelectedPageName] = useState("No page" as PageNamesType);

  let [loggedIn, setLoggedIn] = useState(false);
  let [username, setUsername] = useState("");
  let [storylines, setStorylines] = useState([] as string[]);
  let [selectedStoryline, setSelectedStoryline] = useState("");
  let [roles, setRoles] = useState([] as string[]);
  let [token, setToken] = useState("");
  let [selectedServer, setSelectedServer] = useState("ops server");
  let [bookmarkletCopyMessage, setBookmarkletCopyMessage] = useState("");

  let usernameRef = useRef("");
  let passwordRef = useRef("");

  function login(){
    startLogin(usernameRef?.current.value, passwordRef?.current.value, selectedServer);
  }


  async function copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    }
    catch {
      // Fall through to execCommand fallback.
    }

    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textArea);
      return copied;
    }
    catch {
      return false;
    }
  }


  async function copyBookmarklet(toolName: string, code: string): Promise<void> {
    const copied = await copyToClipboard(code);
    setBookmarkletCopyMessage(
      copied
        ? `${toolName} code copied. Create a bookmark manually and paste it into the URL field.`
        : `Could not copy ${toolName} code automatically.`
    );
  }


  function handleKeydown(event: KeyboardEvent){
    if(event.key == "Enter"){
      login();
    }
  }


  JsonPostQueryHelper.setErrorHandler(logoff);

  function logoff(): void{
    setLoggedIn(false);
    setToken("");
    setSelectedPageName("No page");
  }

  function startLogin(username: string, password: string, server: string) {
    if(server == "test server"){
      StorylineServerProxy.setURLBase(StorylineServerProxy.TEST_SERVER_URL_BASE)
    }
    else if(server == "ops server"){
      StorylineServerProxy.setURLBase(StorylineServerProxy.OPS_SERVER_URL_BASE)
    }
    else{
      StorylineServerProxy.setURLBase(StorylineServerProxy.LOCALHOST_URL_BASE);
    }
    StorylineServerProxy.authenticate(username, password).then((data) => {
        if(data.token){
          let token = JSON.parse(window.atob(data.token.split('.')[1]));
          setUsername(token.username);
          setRoles(token.roles);
          setStorylines(["", ...token.storylines]);
          setSelectedStoryline(token.storylines.includes("storyline")?"storyline":"");
          StorylineServerProxy.setToken(data.token);
          setLoggedIn(true);
          setSelectedPageName("No page");
          setToken(data.token);
        }
        else{
          logoff();
          alert(data.message);
        }
      })
      .catch((e) => {
        setLoggedIn(false);
        setSelectedPageName("No page");
        alert(e);
      });
    // setLoggedIn(true);
  }


  let userMenuConfig = [
    { label: "Profile", callback: () => { setSelectedPageName("User > Profile")} },
    { label: "Change password", callback: () => { setSelectedPageName("User > Change password")} },
    {
      label: "Logout",
      callback: () => {
        setSelectedPageName("No page");
        JsonPostQueryHelper.setStoryline("");
        logoff();
      }
    }
  ];

  const searchMenuConfig = [
    { label: "Search", callback: () => { setSelectedPageName("Storyline Search")} }
  ];

  const customersMenuConfig = [
    { label: "Create customer", callback: () => { setSelectedPageName("Customers > Create")} },
    { label: "Edit customer", callback: () => { setSelectedPageName("Customers > Edit")} },
    { label: "Delete customer", callback: () => { setSelectedPageName("Customers > Delete customer")} },
    { label: "Generate reports", callback: () => { setSelectedPageName("Customers > Generate report")} },
    { label: "Multi-storyline reports", callback: () => { setSelectedPageName("Customers > Multi-storyline report")} },
  ];

  const scrapersMenuConfig = [
    { label: "Create scraper ", callback: () => { setSelectedPageName("Scrapers > Create") }  }, 
    { label: "Edit / delete scraper", callback: () => { setSelectedPageName("Scrapers > Edit / delete") } },
    { label: "Run / check status", callback: () => { setSelectedPageName("Scrapers > Run / check status") } },
    { label: "Mass import", callback: () => { setSelectedPageName("Scrapers > Mass import") } },
    { label: "Advanced", callback: () => { setSelectedPageName("Scrapers > Advanced") } }
  ];

  let scrapReportsMenuConfig = [
    { label: "View storyline", callback: () => { setSelectedPageName("Storyline > View") } },
    { label: "Scrap reports (classic)", callback: () => { setSelectedPageName("Storyline > Scrap reports (classic)") }  },
    { label: "Scrap reports", callback: () => { setSelectedPageName("Storyline > Scrap reports") }  },
    { label: "Failed scrap reports", callback: () => { setSelectedPageName("Storyline > Failed scrap reports") }  },
    { label: "Pending TEDs", callback: () => { setSelectedPageName("Storyline > Pending TEDs")} },
    { label: "Manual creation", callback: () => { setSelectedPageName("Storyline > Manual creation") }  },
    { label: "Digest generator", callback: () => { setSelectedPageName("Storyline > Digest generator") }  },
  ]

  let archivingMenuConfig= [
    { label: "Automatic", callback: () => { setSelectedPageName("Archiving > Automatic") }   },
    { label: "Manual", callback: () => { setSelectedPageName("Archiving > Manual") } },
  ];

  let channelsMenuConfig = [
//    { label: "Set channels", callback: () => { setSelectedPageName("Channels > Set") } },
    { label: "Set for DSNA", callback: () => { setSelectedPageName("Channels > DSNA") } },
    { label: "Set for Engage", callback: () => { setSelectedPageName("Channels > Engage") } },
//    { label: "Newsletters", callback: () => { setSelectedPageName("Channels > Newsletters") } },
//    { label: "Social media", callback: () => { setSelectedPageName("Channels > Social media") } },
  ];

  let garMenuConfig = [
    { label: "Pick news", callback: () => { setSelectedPageName("GAR > Pick news") } },
    { label: "Pick jobs", callback: () => { setSelectedPageName("GAR > Pick jobs") } },
    { label: "Other links", callback: () => { setSelectedPageName("GAR > Extra links") } },
  ];

  const TEDsMenuConfig = [
    { label: "Download", callback: () => { setSelectedPageName("TEDs > Download") } },
    { label: "Processed TEDs", callback: () => { setSelectedPageName("TEDs > Processed TEDs")} },
    { label: "Filters", callback: () => { setSelectedPageName("TEDs > Filters")} },
  ]

  const robotsMenuConfig = [
    { label: "Status", callback: () => { setSelectedPageName("Robots > Status") } }
  ]

  const storylineTheme = createTheme({
    components: {
      // Name of the component
      MuiButton: {
        defaultProps: {
          variant: "outlined"
        }
      }
    }
  });
  

  /*
  if(!loggedIn){
    setSelectedPageName("No page");
  }
*/

  function handeStorylineChanged(event){
    setSelectedPageName("No page");
    setSelectedStoryline(event.target.value);
  }

  useEffect(() => {
    JsonPostQueryHelper.setStoryline(selectedStoryline);
  }, [selectedStoryline])

  useEffect(() => {
    if(typeof window == "undefined"){
      return;
    }
    const hostname = window.location.hostname;
    setSelectedServer(hostname == "localhost" ? "localhost" : hostname.indexOf("test") != -1 ? "test server" : "ops server");
  }, [])


  const imageExtractorBookmarklet =
   "javascript:(function(){const t=\"imgcopy-btn\",e=\"imgcopy-wrapper\",n=\"imgcopy-style\",o=\"data-imgcopy-enhanced\";function c(t){try{return new URL(t,document.baseURI).href}catch(e){return t}}function i(){if(document.getElementById(n))return;const o=document.createElement(\"style\");o.id=n,o.textContent=\".\"+e+\"{position:relative;display:inline-block}.\"+t+\"{position:absolute;top:4px;right:4px;background:rgba(0,0,0,0.75);color:#fff;border:none;padding:4px 10px;font-size:13px;border-radius:999px;cursor:pointer;font-family:Arial,sans-serif;z-index:1}.\"+t+\":hover{background:rgba(0,0,0,0.9)}\",document.head.appendChild(o)}function d(t){if(t.parentElement&&t.parentElement.classList.contains(e))return t.parentElement;const n=document.createElement(\"span\");return n.className=e,n.style.position=\"relative\",n.style.display=\"inline-block\",t.parentNode.insertBefore(n,t),n.appendChild(t),n}function r(t){if(navigator.clipboard&&navigator.clipboard.writeText)return navigator.clipboard.writeText(t);return new Promise(e=>{const n=document.createElement(\"textarea\");n.value=t,n.style.position=\"fixed\",n.style.top=\"-9999px\",document.body.appendChild(n),n.select();try{document.execCommand(\"copy\")}catch(t){console.warn(\"Copy failed\",t)}document.body.removeChild(n),e()})}i();let a=0;document.querySelectorAll(\"img\").forEach(n=>{if(n.hasAttribute(o))return;const i=n.currentSrc||n.src;if(!i)return;const s=d(n),l=document.createElement(\"button\");l.className=t,l.type=\"button\",l.textContent=\"Copy\",l.setAttribute(\"data-url\",c(i)),s.appendChild(l),n.setAttribute(o,\"1\"),a++}),document.querySelectorAll(\"*\").forEach(e=>{if(e.hasAttribute(o))return;const n=getComputedStyle(e).backgroundImage;if(!n||\"none\"===n)return;const i=/url\\([\"']?(.*?)[\"']?\\)/.exec(n);if(!i||!i[1])return;const s=c(i[1]);if(!s)return;const l=e.getBoundingClientRect();if(l.width<40||l.height<40)return;\"static\"===getComputedStyle(e).position&&(e.style.position=\"relative\");const r=document.createElement(\"button\");r.className=t,r.type=\"button\",r.textContent=\"Copy\",r.setAttribute(\"data-url\",s),e.appendChild(r),e.setAttribute(o,\"1\"),a++}),window.__imgcopy_listener_added||(document.addEventListener(\"click\",n=>{const o=n.target;if(!o.classList||!o.classList.contains(t))return;const i=o.getAttribute(\"data-url\");if(!i)return;const c=o.textContent;r(i).then(()=>{o.textContent=\"Copied!\",setTimeout(()=>{o.textContent=c;window.close()},800)})}),window.__imgcopy_listener_added=!0),console.log(\"Copy buttons added on \"+a+\" element(s).\")})();";


 const garClipperBookmarklet = `javascript:(function(){var baseAddress="https://www.globalairspaceradar.com";var formEndpoint=baseAddress+"/?gar_clip=1";var STYLE_ID="wpclip-inline-style";var CHECK_CLASS="wpclip-check";var WRAP_CLASS="wpclip-wrapper";var ORDER_BADGE_CLASS="wpclip-order-badge";var PREVIEW_BTN_ID="wpclip-preview-selected";var PREVIEW_OVERLAY_ID="wpclip-preview-overlay";var TITLE_INPUT_ID="wpclip-gar-title";var selectionCounter=1;var existingPrev=document.getElementById(PREVIEW_BTN_ID);if(existingPrev){document.querySelectorAll("."+WRAP_CLASS).forEach(function(wrap){var el=wrap.firstElementChild;if(el){el.classList.remove("wpclip-highlight");wrap.parentNode.insertBefore(el,wrap);}wrap.remove();});var st=document.getElementById(STYLE_ID);if(st)st.remove();existingPrev.remove();var overlayOld=document.getElementById(PREVIEW_OVERLAY_ID);if(overlayOld)overlayOld.remove();delete window.__wpclipMap;delete window.__wpclipSelectionHandlerAdded;return;}if(!document.getElementById(STYLE_ID)){var style=document.createElement("style");style.id=STYLE_ID;style.textContent="."+WRAP_CLASS+"{position:relative;display:inline-block;}.wpclip-highlight{outline:2px solid #f39c12;outline-offset:2px;}."+CHECK_CLASS+"{position:absolute;top:4px;right:4px;transform:scale(1.3);z-index:2147483647;background:#fff;border-radius:3px;cursor:pointer;}."+ORDER_BADGE_CLASS+"{position:absolute;top:4px;right:32px;background:#4caf50;color:#fff;border-radius:20px;font-size:11px;padding:0 6px;z-index:2147483647;}#"+PREVIEW_BTN_ID+"{position:fixed;bottom:16px;right:16px;padding:6px 12px;font-size:13px;background:#fff;border:1px solid #777;z-index:2147483647;cursor:pointer;border-radius:4px;}#"+PREVIEW_BTN_ID+".wpclip-empty{border-color:#f44336;color:#f44336;}#"+PREVIEW_OVERLAY_ID+"{position:fixed;inset:0;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;z-index:2147483647;}.wpclip-preview-modal{background:#fff;width:90%;max-width:900px;max-height:85vh;overflow:hidden;border-radius:8px;display:flex;flex-direction:column;font-family:Arial,sans-serif;font-size:13px;}.wpclip-preview-header{padding:10px;border-bottom:1px solid #ddd;background:#f5f5f5;font-size:15px;font-weight:bold;display:flex;justify-content:space-between;align-items:center;}.wpclip-preview-titlebar{padding:8px 10px;border-bottom:1px solid #eee;background:#fafafa;display:flex;flex-direction:column;gap:4px;}.wpclip-preview-titlebar label{font-size:12px;color:#444;}.wpclip-preview-titlebar input{padding:5px 8px;font-size:13px;width:100%;box-sizing:border-box;}.wpclip-preview-body{padding:12px;overflow:auto;flex:1 1 auto;}.wpclip-preview-footer{padding:10px;border-top:1px solid #ddd;background:#fafafa;display:flex;justify-content:flex-end;gap:8px;}.wpclip-preview-close,.wpclip-preview-cancel,.wpclip-preview-send-silent,.wpclip-preview-send-edit{cursor:pointer;border:1px solid #777;background:#fff;padding:5px 10px;border-radius:4px;font-size:12px;}.wpclip-preview-body h1{font-size:24px;margin:10px 0;}.wpclip-preview-body h2{font-size:20px;margin:10px 0;}.wpclip-preview-body p{font-size:14px;line-height:1.4;margin:10px 0;}.wpclip-preview-body blockquote{border-left:4px solid #ccc;background:#f7f7f7;padding:10px;margin:10px 0;font-style:italic;}.wpclip-preview-body img{max-width:100%;display:block;margin:10px 0;}";document.head.appendChild(style);}function stripQuotesInBlockquote(root){function walk(node){node.childNodes.forEach(function(child){if(child.nodeType===3){var txt=child.nodeValue;txt=txt.replace(/^[“”"']+/,"").replace(/[“”"']+$/,"");child.nodeValue=txt;}else if(child.nodeType===1){walk(child);}});}walk(root);}function cleanEl(root){var forbidden=new Set(["EM","STRONG","B","FONT"]);(function recur(n){if(n.nodeType!==1)return;var el=n;var c=el.firstChild;while(c){var nx=c.nextSibling;recur(c);c=nx;}if(forbidden.has(el.tagName)){var p=el.parentNode;while(el.firstChild)p.insertBefore(el.firstChild,el);p.removeChild(el);return;}if(el.tagName==="IMG"){var src=el.getAttribute("src");var alt=el.getAttribute("alt")||"";if(src&&src.indexOf("data:")!==0&&!/^https?:\\/\\//i.test(src)){if(src.indexOf("//")===0){src=location.protocol+src;}else{var base=location.origin.replace(/\\/$/,"");if(src.charAt(0)!=="/"){src="/"+src;}src=base+src;}}while(el.attributes.length)el.removeAttribute(el.attributes[0].name);if(src)el.setAttribute("src",src);if(alt)el.setAttribute("alt",alt);return;}["style","class","face","color","size"].forEach(function(a){el.removeAttribute(a);});})(root);if(root.tagName==="BLOCKQUOTE"){stripQuotesInBlockquote(root);}return root.outerHTML;}var selectors="h1,h2,h3,h4,h5,h6,p,img,blockquote,ul,ol";var nodes=Array.prototype.slice.call(document.querySelectorAll(selectors));var items=[];nodes.forEach(function(el,i){if(el.classList.contains(WRAP_CLASS))return;if(el.tagName==="IMG"){if(el.width<40&&el.height<40)return;items.push({key:"wpe-"+i,el:el,html:cleanEl(el.cloneNode(true))});}else{var t=(el.innerText||"").trim();if(!t||t.length<10)return;items.push({key:"wpe-"+i,el:el,html:cleanEl(el.cloneNode(true))});}});if(!items.length){alert("No selectable content found.");return;}window.__wpclipMap={};items.forEach(function(it){var el=it.el;if(!el.parentNode)return;var w=document.createElement("span");w.className=WRAP_CLASS;el.classList.add("wpclip-highlight");el.parentNode.insertBefore(w,el);w.appendChild(el);var badge=document.createElement("span");badge.className=ORDER_BADGE_CLASS;w.appendChild(badge);var cb=document.createElement("input");cb.type="checkbox";cb.className=CHECK_CLASS;cb.setAttribute("data-key",it.key);w.appendChild(cb);window.__wpclipMap[it.key]=it.html;});function updateOrder(){var cbs=Array.prototype.slice.call(document.querySelectorAll("."+CHECK_CLASS));var sel=[];cbs.forEach(function(cb){if(!cb.checked){cb.removeAttribute("data-order");var bd=cb.parentNode.querySelector("."+ORDER_BADGE_CLASS);if(bd)bd.textContent="";}else{var o=parseInt(cb.getAttribute("data-order")||"0",10);if(o>0)sel.push({cb:cb,order:o});}});sel.sort(function(a,b){return a.order-b.order;});sel.forEach(function(e,i){var n=i+1;e.cb.setAttribute("data-order",String(n));var bd=e.cb.parentNode.querySelector("."+ORDER_BADGE_CLASS);if(bd)bd.textContent=String(n);});selectionCounter=sel.length+1;}if(!window.__wpclipSelectionHandlerAdded){document.addEventListener("change",function(e){var cb=e.target;if(!cb.classList||!cb.classList.contains(CHECK_CLASS))return;if(cb.checked&&!cb.getAttribute("data-order")){cb.setAttribute("data-order",String(selectionCounter++));}updateOrder();});window.__wpclipSelectionHandlerAdded=true;}function build(){var sel=Array.prototype.slice.call(document.querySelectorAll("."+CHECK_CLASS+":checked"));if(!sel.length)return{n:0,html:""};sel.sort(function(a,b){return parseInt(a.getAttribute("data-order")||"0",10)-parseInt(b.getAttribute("data-order")||"0",10);});var parts=sel.map(function(cb){return window.__wpclipMap[cb.getAttribute("data-key")]});return{n:sel.length,html:parts.join("\\n\\n")};}function convertToBlocks(html){var container=document.createElement("div");container.innerHTML=html;var out=[];Array.prototype.forEach.call(container.children,function(el){var tag=el.tagName;var segment=el.outerHTML;if(tag==="P"){out.push("<!-- wp:paragraph -->"+segment+"<!-- /wp:paragraph -->");}else if(tag==="H1"||tag==="H2"||tag==="H3"||tag==="H4"||tag==="H5"||tag==="H6"){var level=parseInt(tag.charAt(1),10)||2;out.push("<!-- wp:heading {\\\"level\\\":"+level+"} -->"+segment+"<!-- /wp:heading -->");}else if(tag==="BLOCKQUOTE"){var inner=el.innerHTML.trim();var paraBlock;if(/^<p[\\s>]/i.test(inner)&&/<\\/p>\\s*$/.test(inner)&&inner.indexOf("<!-- wp:")===-1){paraBlock="<!-- wp:paragraph -->"+inner+"<!-- /wp:paragraph -->";}else{paraBlock="<!-- wp:paragraph --><p>"+inner+"</p><!-- /wp:paragraph -->";}var q='<blockquote class=\\"wp-block-quote\\">'+paraBlock+'</blockquote>';out.push("<!-- wp:quote -->"+q+"<!-- /wp:quote -->");}else if(tag==="IMG"){out.push("<!-- wp:image --><figure class=\\"wp-block-image\\">"+segment+"</figure><!-- /wp:image -->");}else if(tag==="UL"||tag==="OL"){out.push("<!-- wp:list -->"+segment+"<!-- /wp:list -->");}else{out.push(segment);}});return out.join("\\n\\n");}function sendToGARSilent(title,html){var url=formEndpoint+"&gar_mode=silent"+"&gar_title="+encodeURIComponent(title)+"&gar_content="+encodeURIComponent(html)+"&gar_source="+encodeURIComponent(location.href);window.open(url,"_blank","width=380,height=200,noopener,noreferrer");}function sendToGAREdit(title,html){var url=formEndpoint+"&gar_title="+encodeURIComponent(title)+"&gar_content="+encodeURIComponent(html)+"&gar_source="+encodeURIComponent(location.href);window.open(url,"_blank");}var prev=document.createElement("button");prev.id=PREVIEW_BTN_ID;prev.textContent="Preview selected";document.body.appendChild(prev);prev.onclick=function(){var r=build();if(!r.n){prev.classList.add("wpclip-empty");prev.textContent="Nothing selected";setTimeout(function(){prev.classList.remove("wpclip-empty");prev.textContent="Preview selected";},1200);return;}var title=(document.title||"GAR Draft").replace(/\\s*\\|.*$/,"").trim();var ov=document.createElement("div");ov.id=PREVIEW_OVERLAY_ID;ov.innerHTML='<div class="wpclip-preview-modal"><div class="wpclip-preview-header"><span>Preview for GAR</span><button class="wpclip-preview-close">✕</button></div><div class="wpclip-preview-titlebar"><label for="'+TITLE_INPUT_ID+'">GAR title:</label><input type="text" id="'+TITLE_INPUT_ID+'" value="'+title.replace(/\"/g,"&quot;")+'"></div><div class="wpclip-preview-body">'+r.html+'</div><div class="wpclip-preview-footer"><button class="wpclip-preview-cancel">Close</button><button class="wpclip-preview-send-silent">Send to GAR</button><button class="wpclip-preview-send-edit">Send &amp; edit in GAR</button></div></div>';document.body.appendChild(ov);ov.onclick=function(e){if(e.target.classList.contains("wpclip-preview-close")||e.target.classList.contains("wpclip-preview-cancel")||e.target===ov){ov.remove();}if(e.target.classList.contains("wpclip-preview-send-silent")){var tInput=document.getElementById(TITLE_INPUT_ID);var t=(tInput&&tInput.value.trim())||title;var blockHtml=convertToBlocks(r.html);e.target.textContent="Sent!";sendToGARSilent(t,blockHtml);setTimeout(function(){ov.remove();},600);}if(e.target.classList.contains("wpclip-preview-send-edit")){var tInput2=document.getElementById(TITLE_INPUT_ID);var t2=(tInput2&&tInput2.value.trim())||title;var blockHtml2=convertToBlocks(r.html);e.target.textContent="Opening…";sendToGAREdit(t2,blockHtml2);setTimeout(function(){ov.remove();},600);}}};})();`;


  return (
    <div className="App">
        <AppBar color="transparent" position="static" sx={{ marginBottom: 2}}>
          <Toolbar>
            {loggedIn ? 
              <>
                <Button onClick={ () => { setSelectedPageName("No page") }}>Home</Button>
                <Select id="storylineSelector" value={selectedStoryline} onChange={handeStorylineChanged}>
                  {
                    storylines.map(storyline => {
                      return (
                        <MenuItem value={storyline}>{storyline}</MenuItem>
                      )
                    })
                  }
                </Select>

                { /* <BasicMenu menuTitle="User" menuConfig={userMenuConfig} /> */}
                {/* selectedStoryline != ""?<BasicMenu menuTitle="Search" menuConfig={searchMenuConfig} />:<></> */ }                
                {
                  /*
                  (roles.includes("FoxATM")) && (selectedStoryline != "")?
                  <>
                    <BasicMenu menuTitle="Scrapers" menuConfig={scrapersMenuConfig} />
                    <BasicMenu menuTitle="Storyline" menuConfig={scrapReportsMenuConfig} />
                    <BasicMenu menuTitle="Archiving" menuConfig={archivingMenuConfig} />
                    <BasicMenu menuTitle="Customers" menuConfig={customersMenuConfig} />
                    <BasicMenu menuTitle="Channels" menuConfig={channelsMenuConfig} />
                    <BasicMenu menuTitle="TEDs" menuConfig={TEDsMenuConfig} />
                  </>:
                  <></>
                  */
                }
                {
                    // roles.includes("GAR") && selectedStoryline != ""?<BasicMenu menuTitle="GAR" menuConfig={garMenuConfig} />:<></>
                }
                {
                    // roles.includes("FoxATM")?<BasicMenu menuTitle="Robots" menuConfig={robotsMenuConfig} />:<></>
                }

                <Typography>{ selectedPageName }</Typography>
              </>
            : (
              <>
                Login to access the storyline
              </>
            )}
          </Toolbar>
        </AppBar>
        <Container>
          <ThemeProvider theme={storylineTheme}>
          {loggedIn?<>
          <input id="tokenInputId"  type="hidden" value={token}></input>
            {
              selectedPageName == "No page" ? <>
                <h3>Search</h3>
                <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline Search")}>Search</Button><br/>
                <h3>User</h3>
                <Button variant={"outlined"} onClick={() => setSelectedPageName("User > Profile")}>Profile</Button>
                <Button variant={"outlined"} onClick={() => setSelectedPageName("User > Change password")}>Change password</Button>
                <Button variant={"outlined"} onClick={() => { setSelectedPageName("No page");
                                                              JsonPostQueryHelper.setStoryline("");
                                                              logoff();
                                                              }}>Logout</Button><br/>
                                                                              {
                roles.includes("GAR") && selectedStoryline != ""?<>
                      <h3>GAR</h3>
                        <Button variant={"outlined"} onClick={() => setSelectedPageName("GAR > Pick news")}>Pick news</Button>
                        <Button variant={"outlined"} onClick={() => setSelectedPageName("GAR > Pick jobs")}>Pick jobs</Button>
                        <Button variant={"outlined"} onClick={() => setSelectedPageName("GAR > Extra links")}>Other links</Button><br/>
                  </>:<></>
                }
                {
                roles.includes("FoxATM") && selectedStoryline != ""?<>

                  <h3>Scrapers</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Scrapers > Create")}>Create</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Scrapers > Edit / delete")}>Edit / delete</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Scrapers > Run / check status")}>Run / check status</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Scrapers > Mass import")}>Mass import</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Scrapers > Advanced")}>Advanced</Button><br/>
                  <h3>Storyline</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > View")}>View storyline</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Scrap reports (classic)")}>Scrap reports (classic)</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Scrap reports")}>Scrap reports</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Failed scrap reports")}>Failed scrap reports</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Pending TEDs")}>Pending TEDs</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Manual creation")}>Manual creation</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Storyline > Digest generator")}>Digest generator</Button><br/>
                  <h3>Archiving</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Archiving > Automatic")}>Automatic</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Archiving > Manual")}>Manual</Button><br/>
                  <h3>Customers</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Customers > Create")}>Create customer</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Customers > Edit")}>Edit customer</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Customers > Delete customer")}>Delete customer</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Customers > Generate report")}>Generate reports</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Customers > Multi-storyline report")}>Multi-storyline reports</Button><br/>
                  <h3>Channels</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Channels > DSNA")}>Set for DSNA</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Channels > Engage")}>Set for Engage</Button>
                  <h3>TEDs</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("TEDs > Download")}>Download</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("TEDs > Processed TEDs")}>Processed TEDs</Button>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("TEDs > Filters")}>Filters</Button><br/>
                  <h3>Robots</h3>
                  <Button variant={"outlined"} onClick={() => setSelectedPageName("Robots > Status")}>Status</Button><br/>
                  <h3>Tools</h3>
                  <Typography sx={{ marginBottom: 1 }}>
                    Bookmarklet setup:
                  </Typography>
                  <Typography component="div" sx={{ marginBottom: 1 }}>
                    <ol style={{ marginTop: 0, marginBottom: 0, paddingLeft: "1.2rem" }}>
                      <li>Click one of the copy buttons below.</li>
                      <li>Create a new bookmark in your browser.</li>
                      <li>Edit that bookmark and paste the copied code into the URL field.</li>
                      <li>Save the bookmark, open any article page, and click the bookmark to run it.</li>
                    </ol>
                  </Typography>
                  <Button variant={"outlined"} sx={{ marginRight: 1, marginBottom: 1 }} onClick={() => copyBookmarklet("Image extractor", imageExtractorBookmarklet)}>
                    Copy Image extractor code
                  </Button>
                  <Button variant={"outlined"} sx={{ marginBottom: 1 }} onClick={() => copyBookmarklet("Draft generator", garClipperBookmarklet)}>
                    Copy Draft generator code
                  </Button>
                  {
                    bookmarkletCopyMessage == "" ? <></> : <Typography sx={{ marginBottom: 1 }}>{bookmarkletCopyMessage}</Typography>
                  }
                </>:<></>
              }
              </>
              : selectedPageName == "User > Profile" ? <UserProfileForm username={username}/>
              : selectedPageName == "Storyline Search" ? <StorylineSearch />
              : selectedPageName == "User > Change password" ? <PasswordChanger username={username}/>
              : selectedPageName == "Scrapers > Create" ? <ScraperCreator />
              : selectedPageName == "Scrapers > Edit / delete" ? <ScrapersEditor />
              : selectedPageName == "Scrapers > Run / check status" ? <RunScrapers  />      
              : selectedPageName == "Scrapers > Mass import" ? <ScrapersMassImport  />      
              : selectedPageName == "Scrapers > Advanced" ? <ScrapReportAdvanced  />      
              : selectedPageName == "Storyline > View" ? <StoryElementsList />     
              : selectedPageName == "Storyline > Scrap reports (classic)" ? <ScrapReportsPageClassic />      
              : selectedPageName == "Storyline > Scrap reports" ? <ScrapReportsPage failedReports={false} />      
              : selectedPageName == "Storyline > Failed scrap reports" ? <ScrapReportsPage failedReports={true} />      
              : selectedPageName == "Storyline > Pending TEDs" ? <PendingTEDsList showProcessedTEDs={false} />      
              : selectedPageName == "Storyline > Manual creation" ? <LinkDetails link="" scrapReport={null} handleCloseClicked={null} />      
              : selectedPageName == "Storyline > Digest generator" ? <DigestGenerator />      
              : selectedPageName == "Archiving > Automatic" ? <AutomaticArchiveManagement />      
              : selectedPageName == "Archiving > Manual" ? <ManualArchiveManagement />      
              : selectedPageName == "Customers > Create" ? <CustomerCreator />      
              : selectedPageName == "Customers > Edit" ? <CustomerEditor />      
              : selectedPageName == "Customers > Generate report" ? <ReportGenerator loggedInUser={username} />      
              : selectedPageName == "Customers > Multi-storyline report" ? <MultiStorylineReport allStorylines={storylines}/>
              : selectedPageName == "Customers > Delete customer" ? <CustomerDeletor />      
            //  : selectedPageName == "Channels > Set" ? <ChannelsSetterPage />      
              : selectedPageName == "Channels > DSNA" ? <><DSNAChannelSetterPage /></>    
              : selectedPageName == "Channels > Engage" ? <EngageChannelSetterPage />  
            //  : selectedPageName == "Channels > Newsletters" ? <Newsletters />      
            //  : selectedPageName == "Channels > Social media" ? <ElementsForSocialMedia />      
              : selectedPageName == "GAR > Pick news" ? <GARSetterPage filter={{type: "News"}} />   
              : selectedPageName == "GAR > Pick jobs" ? <GARSetterPage filter={{type: "Job"}} />   
              : selectedPageName == "GAR > Extra links" ? <GARExtraLinksList />  
              : selectedPageName == "TEDs > Download" ? <TEDsDownloadPage />      
              : selectedPageName == "TEDs > Filters" ? <TEDFiltersPage />      
              : selectedPageName == "Robots > Status" ? <RobotsList />      
            //  : selectedPageName == "TEDs > Processed TEDs" ? <PendingTEDsList showProcessedTEDs={true} />      
              : <></>
            }

            {
              // NOTE: this one is separated to allow for a toggle between pending TEDs and processed TEDs
              selectedPageName == "TEDs > Processed TEDs" ? <PendingTEDsList showProcessedTEDs={true} />:<></>      
            }
            </>:<>
        <TextField
        variant="filled"
        label="Username"
        inputRef={usernameRef}
        defaultValue="vincent"
        sx={{width:"100%"}}
      ></TextField>
      <br/>
      <TextField
        variant="filled"
        label="Password"
        onKeyDown={handleKeydown}
        inputRef={passwordRef}
        type="password"
        sx={{width:"100%"}}
      ></TextField>
      <br/>
      <Button
      variant={"outlined"}
        onClick={login}
        sx={{width:"100%"}}
      >
        Login
      </Button>
      <br/><br/>
      <Typography>
        Connect to:
      </Typography>
      <Select 
        sx={{width:"100%"}}
        value={selectedServer}
        onChange={(event: any) => setSelectedServer(event.target.value)}>
        <MenuItem value="ops server">Production server</MenuItem>
        <MenuItem value="localhost">Localhost</MenuItem>
        <MenuItem value="test server">Test server</MenuItem>
      </Select>

            </>
            }
          </ThemeProvider>
        </Container>
    </div>
  );
}
